import PropTypes from 'prop-types';
import React from 'react';
import { Notify, appContext } from '@resy/ui-kitchen';
import AnnouncementContainer from '../AnnouncementContainer';
import { GdaOnboardingModal } from '../../components/GdaOnboarding';
import ReservationCancelModal from '../../components/ReservationCancelModal';
import { AuthContainerModal } from '../AuthContainer';
import { GDAConciergeBookingModal } from '../../components/GDAConciergeBooking';
import TockInterstitialModal from '../../components/TockInterstitial';
import TockExperienceOptionsModal from '../../components/TockExperienceOptions';
import TockExperienceOfferingModal from '../../components/TockExperienceOffering';
import { parameterize } from '../../../../../ui-kitchen/src/utils';
import { UpdatePhoneModal } from '../../../../../ui-kitchen/src/components/Account/UpdatePhone/UpdatePhoneContainer';
import { GatingMessageModal } from '../../../../../ui-kitchen/src/components/Gating/GatingMessageModal';

/* istanbul ignore next */
const NOOP = () => {};
const MODALS = {
  ONBOARDING_LITE: 'onboarding-lite',
  RESERVATION_CANCEL: 'reservation-cancel',
  SIGNIN: 'signin',
  SIGNUP: 'signup',
  CONCIERGE_BOOKING: 'concierge-booking',
  NOTIFY: 'notify',
  UPDATE_PHONE: 'update-phone',
  GATING_ERROR: 'gating-error',
  ANNOUNCEMENT_MODAL: 'announcement-modal',
};

const ModalsContainer = ({
  $rootScope,
  $location,
  $scope,
  $window,
  $route,
  activeNotifications,
  Analytics,
  AutomationMode,
  isMobile,
  clearNotifyParams,
  closeModal,
  gdaUrl,
  hasViewedWelcome,
  isGdaUser,
  isCanceled,
  errors,
  loadReservations,
  modal,
  notifyIsOpen,
  postToUser,
  cancelToken,
  onCloseNotifyModal,
  reservation,
  reservations,
  ResyAmexConfig,
  selectedDate,
  selectedQuantity,
  successCallback,
  cancelReservation,
  serviceTypeId,
  venueCitySlug,
  venueCityName,
  venueId,
  venueName,
  venueUrl,
  eventName,
  callingCode,
}) => {
  let modalToUse = modal;

  if (!cancelToken && isGdaUser && !hasViewedWelcome) {
    modalToUse = MODALS.ONBOARDING_LITE;
  }

  const renderActiveModal = () => {
    switch (modalToUse) {
      case (MODALS.RESERVATION_CANCEL): {
        return (
          <ReservationCancelModal
            $location={$location}
            Analytics={Analytics}
            closeModal={closeModal}
            isCanceled={isCanceled}
            isMobile={isMobile}
            reservation={reservation}
            cancelReservation={cancelReservation}
            errors={errors}
          />
        );
      }

      case (MODALS.SIGNIN): {
        return (
          <AuthContainerModal
            $location={$location}
            $rootScope={$rootScope}
            $route={$route}
            Analytics={Analytics}
            AutomationMode={AutomationMode}
            closeModal={closeModal}
            callingCode={callingCode}
            successCallback={successCallback}
            ResyAmexConfig={ResyAmexConfig}
          />
        );
      }

      case (MODALS.SIGNUP): {
        return (
          <AuthContainerModal
            $location={$location}
            $rootScope={$rootScope}
            $route={$route}
            Analytics={Analytics}
            AutomationMode={AutomationMode}
            closeModal={closeModal}
            firstStep={MODALS.SIGNUP}
            successCallback={successCallback}
            ResyAmexConfig={ResyAmexConfig}
          />
        );
      }

      case (MODALS.ONBOARDING_LITE): {
        postToUser(parameterize({ viewed_gda_welcome: true }));
        return (
          <GdaOnboardingModal
            closeModal={closeModal}
            gdaUrl={gdaUrl}
            isMobile={isMobile}
            isGdaUser={isGdaUser}
          />
        );
      }

      case (MODALS.CONCIERGE_BOOKING): {
        return (
          <GDAConciergeBookingModal
            closeModal={closeModal}
            isMobile={isMobile}
          />
        );
      }

      case (MODALS.NOTIFY): {
        return (
          <Notify
            $rootScope={$rootScope}
            $route={$route}
            Analytics={Analytics}
            $scope={$scope}
            $window={$window}
            activeNotifications={activeNotifications}
            clearNotifyParams={clearNotifyParams}
            context={appContext.CONSUMER}
            isGda={isGdaUser}
            isOpen={notifyIsOpen}
            loadReservations={loadReservations}
            openModal={onCloseNotifyModal}
            reservations={reservations}
            selectedDate={selectedDate}
            selectedQuantity={selectedQuantity}
            serviceTypeId={serviceTypeId}
            venueCitySlug={venueCitySlug}
            venueCityName={venueCityName}
            venueId={venueId}
            venueName={venueName}
            venueUrl={venueUrl}
            eventName={eventName}
          />
        );
      }
      case (MODALS.UPDATE_PHONE): {
        return (
          <UpdatePhoneModal
            Analytics={Analytics}
            closeModal={closeModal}
            context="modal"
            successCallback={successCallback}
          />
        );
      }
      case (MODALS.GATING_ERROR): {
        return (
          <GatingMessageModal
            Analytics={Analytics}
            AutomationMode={AutomationMode}
            closeModal={closeModal}
            ResyAmexConfig={ResyAmexConfig}
          />
        );
      }
      case (MODALS.ANNOUNCEMENT_MODAL): {
        return (
          <AnnouncementContainer
            $rootScope={$rootScope}
            Analytics={Analytics}
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <>
      {renderActiveModal()}
      <TockInterstitialModal Analytics={Analytics} />
      <TockExperienceOptionsModal />
      <TockExperienceOfferingModal />
    </>
  );
};

const modalsContainerPropTypes = {
  $scope: PropTypes.shape({}).isRequired,
  $window: PropTypes.shape({}).isRequired,
  $rootScope: PropTypes.shape({
    $on: PropTypes.func.isRequired,
  }).isRequired,
  $location: PropTypes.shape({
    search: PropTypes.func.isRequired,
  }).isRequired,
  $route: PropTypes.shape({
    reload: PropTypes.func,
  }).isRequired,
  activeNotifications: PropTypes.shape({
    [PropTypes.number]: PropTypes.shape({
      start: PropTypes.string,
      end: PropTypes.string,
    }),
  }),
  Analytics: PropTypes.shape({ event: PropTypes.func }).isRequired,
  AutomationMode: PropTypes.bool.isRequired,
  callingCode: PropTypes.string,
  cancelReservation: PropTypes.func.isRequired,
  clearNotifyParams: PropTypes.func,
  closeModal: PropTypes.func.isRequired,
  gdaUrl: PropTypes.string,
  hasViewedWelcome: PropTypes.bool,
  isGdaUser: PropTypes.bool,
  loadReservations: PropTypes.func,
  modal: PropTypes.string,
  postToUser: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  isCanceled: PropTypes.bool,
  cancelToken: PropTypes.string,
  notifyIsOpen: PropTypes.bool,
  onCloseNotifyModal: PropTypes.func,
  reservation: PropTypes.shape({}),
  reservations: PropTypes.arrayOf(PropTypes.shape({
    available: PropTypes.arrayOf(
      PropTypes.shape({
        time: PropTypes.string,
      })
    ),
  })),
  ResyAmexConfig: PropTypes.shape({
    baseRequestUrl: PropTypes.string.isRequired,
  }).isRequired,
  errors: PropTypes.shape({}),
  selectedDate: PropTypes.string,
  selectedQuantity: PropTypes.number,
  successCallback: PropTypes.func,
  serviceTypeId: PropTypes.number,
  venueCitySlug: PropTypes.string,
  venueUrl: PropTypes.string,
  venueCityName: PropTypes.string,
  venueId: PropTypes.number,
  venueName: PropTypes.string,
  eventName: PropTypes.string,
};

ModalsContainer.propTypes = modalsContainerPropTypes;
ModalsContainer.defaultProps = {
  activeNotifications: {},
  clearNotifyParams: NOOP,
  callingCode: '1',
  gdaUrl: undefined,
  hasViewedWelcome: false,
  isCanceled: false,
  isGdaUser: false,
  loadReservations: NOOP,
  modal: undefined,
  isMobile: false,
  cancelToken: null,
  notifyIsOpen: true,
  onCloseNotifyModal: NOOP,
  reservation: null,
  reservations: [],
  selectedDate: undefined,
  selectedQuantity: 2,
  errors: null,
  successCallback: NOOP,
  serviceTypeId: undefined,
  venueCitySlug: undefined,
  venueCityName: undefined,
  venueId: undefined,
  venueName: undefined,
  venueUrl: undefined,
  eventName: undefined,
};

export { modalsContainerPropTypes };
export default ModalsContainer;
